/**
 * AssistantWidget, plovoucí AI Poradce M&Ai, dostupný na všech stránkách portálu.
 * Pomáhá s vyplněním formuláře i s orientací (tarify, audit, valuace).
 * Volá /api/assistant (Haiku). Kontext + nabízené dotazy se mění podle stránky.
 */
import React, { useState, useRef, useEffect } from 'react';
import { askAssistant } from '../lib/apiFetch';

const ACCENT = 'var(--purple-glow)';

export function AssistantWidget({ context, suggestions = [], teaserKey = 'global' }) {
  const [open, setOpen] = useState(false);
  const [teaserHidden, setTeaserHidden] = useState(() => {
    try { return sessionStorage.getItem('mnai_poradce_teaser') === '1'; } catch { return false; }
  });
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [msgs, setMsgs] = useState([]); // {role:'user'|'bot', text}
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [msgs, busy]);

  const dismissTeaser = () => {
    setTeaserHidden(true);
    try { sessionStorage.setItem('mnai_poradce_teaser', '1'); } catch { /* ignore */ }
  };

  const ask = async (question) => {
    const text = (question || '').trim();
    if (!text || busy) return;
    setMsgs(m => [...m, { role: 'user', text }]);
    setQ(''); setBusy(true);
    try {
      const { answer } = await askAssistant(text, context);
      setMsgs(m => [...m, { role: 'bot', text: answer || 'Omlouvám se, nepodařilo se odpovědět.' }]);
    } catch (err) {
      setMsgs(m => [...m, { role: 'bot', text: 'Poradce je teď nedostupný. Zkuste to za chvíli, nebo nám napište přes Konzultaci.' }]);
    } finally { setBusy(false); }
  };

  const send = (e) => { e.preventDefault(); ask(q); };
  const openWidget = () => { dismissTeaser(); setOpen(true); };

  if (!open) {
    return (
      <div style={launchWrap}>
        {!teaserHidden && (
          <div style={teaser}>
            <button onClick={dismissTeaser} style={teaserClose} aria-label="Zavřít">×</button>
            <div style={{ fontWeight: 700, color: 'var(--star-white)', marginBottom: '0.15rem' }}>Nevíte si rady?</div>
            <div style={{ color: 'var(--text-dim)', lineHeight: 1.45 }}>
              Jsem váš AI Poradce. Poradím s vyplněním čísel, vysvětlím pojmy i tarify. Zeptejte se mě.
            </div>
            <button onClick={openWidget} style={teaserCta}>Zeptat se Poradce →</button>
          </div>
        )}
        <button onClick={openWidget} style={fab} title="AI Poradce M&Ai">
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
            <span style={{ fontWeight: 800 }}>Poradce M&Ai</span>
            <span style={{ fontSize: '0.68rem', opacity: 0.9, fontWeight: 600 }}>pomůžu vám →</span>
          </span>
        </button>
      </div>
    );
  }

  const chips = msgs.length === 0 ? suggestions : [];

  return (
    <div style={panel}>
      <div style={header}>
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
          <span style={{ fontWeight: 800 }}>AI Poradce M&Ai</span>
          <span style={{ fontSize: '0.7rem', opacity: 0.9 }}>Pomůžu s vyplněním i orientací</span>
        </div>
        <button onClick={() => setOpen(false)} style={closeBtn} aria-label="Zavřít">×</button>
      </div>
      <div ref={bodyRef} style={body}>
        {msgs.length === 0 && (
          <div style={{ color: 'var(--text-dim)', fontSize: '0.86rem', lineHeight: 1.55, marginBottom: '0.6rem' }}>
            Dobrý den! Jsem váš AI Poradce. Pomůžu vám spočítat hodnotu firmy i zorientovat se.
            Na co se rád zeptáte?
          </div>
        )}
        {chips.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.4rem' }}>
            {chips.map((s, i) => (
              <button key={i} onClick={() => ask(s)} style={chip}>{s}</button>
            ))}
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: '0.5rem' }}>
            <div style={m.role === 'user' ? bubbleUser : bubbleBot}>{m.text}</div>
          </div>
        ))}
        {busy && <div style={{ color: 'var(--text-dim)', fontSize: '0.82rem' }}>Píšu odpověď…</div>}
      </div>
      <form onSubmit={send} style={inputRow}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Napište dotaz…" style={input} />
        <button type="submit" disabled={busy || !q.trim()} style={sendBtn}>→</button>
      </form>
    </div>
  );
}

const launchWrap = { position: 'fixed', right: 18, bottom: 18, zIndex: 50, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.6rem' };
const teaser = {
  position: 'relative', width: 250, background: 'var(--purple-dark)', borderRadius: 14,
  border: '1px solid rgba(91,59,140,0.2)', boxShadow: '0 8px 28px rgba(91,59,140,0.18)',
  padding: '0.85rem 0.95rem', fontSize: '0.82rem', fontFamily: "'Inter', sans-serif",
};
const teaserClose = { position: 'absolute', top: 4, right: 8, background: 'transparent', border: 'none', color: 'var(--text-dim)', fontSize: '1.1rem', cursor: 'pointer', lineHeight: 1 };
const teaserCta = {
  marginTop: '0.6rem', width: '100%', background: ACCENT, color: 'white', border: 'none',
  borderRadius: 8, padding: '0.5rem', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
};
const fab = {
  display: 'flex', alignItems: 'center', gap: '0.55rem',
  background: 'var(--btn-bg)', color: 'var(--btn-text)', border: 'none', borderRadius: 999,
  padding: '0.65rem 1.1rem', fontSize: '0.9rem', cursor: 'pointer',
  boxShadow: '0 6px 20px rgba(91,59,140,0.4)', fontFamily: "'Inter', sans-serif",
};
const panel = {
  position: 'fixed', right: 18, bottom: 18, zIndex: 50, width: 350, maxWidth: 'calc(100vw - 36px)',
  height: 480, maxHeight: 'calc(100vh - 36px)', background: 'var(--purple-dark)', borderRadius: 14,
  border: '1px solid rgba(0,0,0,0.1)', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  display: 'flex', flexDirection: 'column', fontFamily: "'Inter', sans-serif", overflow: 'hidden',
};
const header = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '0.75rem 1rem', background: 'var(--btn-bg)', color: 'var(--btn-text)',
};
const closeBtn = { background: 'transparent', border: 'none', color: 'white', fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 };
const body = { flex: 1, overflowY: 'auto', padding: '0.9rem', background: 'var(--purple-deep)' };
const chip = {
  background: 'var(--purple-dark)', border: '1px solid rgba(91,59,140,0.35)', color: ACCENT, borderRadius: 999,
  padding: '0.35rem 0.7rem', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600, fontFamily: "'Inter', sans-serif",
};
const bubbleBot = { background: 'var(--purple-deep)', color: 'var(--star-white)', padding: '0.55rem 0.75rem', borderRadius: 10, fontSize: '0.86rem', lineHeight: 1.5, maxWidth: '85%', whiteSpace: 'pre-wrap' };
const bubbleUser = { background: ACCENT, color: 'white', padding: '0.55rem 0.75rem', borderRadius: 10, fontSize: '0.86rem', lineHeight: 1.5, maxWidth: '85%' };
const inputRow = { display: 'flex', gap: '0.4rem', padding: '0.6rem', borderTop: '1px solid rgba(0,0,0,0.08)' };
const input = { flex: 1, padding: '0.55rem 0.7rem', border: '1px solid rgba(0,0,0,0.15)', borderRadius: 8, fontSize: '0.88rem', outline: 'none' };
const sendBtn = { background: ACCENT, color: 'white', border: 'none', borderRadius: 8, padding: '0 0.85rem', fontSize: '1.1rem', cursor: 'pointer', fontWeight: 700 };
